/**
 * Monat je Apartment (Board „Monat je Apartment“): am Desktop Raster und Seiten-Editor nebeneinander,
 * am Handy kompaktes Raster mit Auswahlleiste und Blatt (Board „Verfügbarkeit · Handy“). Ohne Edit
 * bleibt der Monat Anzeige; mit Preisautomatik-Rolle lässt sich ein Tag fürs Tagesdetail antippen.
 * Auswahl und Werte gehören dem Apartment und überleben einen Monatswechsel.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AvailabilityDistributionCalendarResponse } from '@/shared/api';
import { useMediaQuery } from '@/shared/hooks/useMediaQuery';
import { cn } from '@/shared/lib/cn';
import { useAutomationLayer, useAutomationStatus } from '../automation/AutomationLayerContext';
import {
  EMPTY_EDITOR_VALUES,
  hasAnyValue,
  type InlineEditorValues,
} from '../calendar/inlineEditorModel';
import type { IsoRange } from '../calendar/rangePickerModel';
import { MonthGrid } from './MonthGrid';
import { MonthApartmentMissing, MonthDesktopLayout } from './MonthLayout';
import { MonthHint, MonthMobileActions } from './MonthMobileActions';
import { MonthSide } from './MonthSide';
import { buildMonth } from './monthModel';
import { useMonthHandover } from './useMonthHandover';
import { useMonthSelection } from './useMonthSelection';
import { CloseoutsOutsideViewNote } from '../CloseoutsOutsideViewNote';

interface Props {
  propertyGroupId: string;
  data: AvailabilityDistributionCalendarResponse;
  apartmentId: string;
  canEdit: boolean;
  /** Ein neues Fenster lädt: das alte steht gedämpft und nimmt keine Auswahl an. */
  loading?: boolean;
  initialRanges?: readonly IsoRange[];
  onHandoverApplied?: () => void;
  onChanged: () => void;
  onDirtyChange: (dirty: boolean) => void;
  notice?: ReactNode;
  /** Handy: Weg zu den Kanalregeln und was sonst nur der Grundkalender zeigt. */
  onManageRules?: () => void;
  mobileExtras?: ReactNode;
}

export function MonthView({ data, apartmentId, canEdit, initialRanges, ...props }: Props) {
  const apartment = data.apartments.find((entry) => entry.propertyId === apartmentId);
  const selection = useMonthSelection(apartmentId);
  const mobile = useMediaQuery('(max-width: 767px)');
  const layer = useAutomationLayer();
  const automationStatus = useAutomationStatus();
  const inspectOnly = !canEdit && (layer !== null || automationStatus !== null);
  const [values, setValues] = useState<InlineEditorValues>(EMPTY_EDITOR_VALUES);
  const [pushed, setPushed] = useState(false);
  const dirty = hasAnyValue(values);
  const { onDirtyChange } = props;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => {
    if (selection.ranges.length > 0) setPushed(false);
  }, [selection.ranges.length]);
  const weeks = useMemo(
    () => (apartment ? buildMonth(apartment, data.channelCloseouts) : []),
    [apartment, data.channelCloseouts],
  );
  const { select, addRange } = selection;
  const applyHandover = useCallback(
    ([first, ...rest]: IsoRange[]) => {
      select(apartmentId, first!.from, first!.to);
      rest.forEach((range) => addRange(range.from, range.to));
    },
    [addRange, apartmentId, select],
  );
  useMonthHandover(initialRanges, data.objectToday, applyHandover, props.onHandoverApplied);

  if (!apartment) return <MonthApartmentMissing />;
  const begin = props.loading
    ? undefined
    : canEdit
      ? selection.begin
      : inspectOnly
        ? (date: string) => select(apartmentId, date, date)
        : undefined;
  const side = (canEdit || inspectOnly) && (
    <MonthSide
      propertyGroupId={props.propertyGroupId}
      apartment={apartment}
      today={data.objectToday}
      canEdit={canEdit}
      inspectOnly={inspectOnly}
      ranges={selection.ranges}
      values={values}
      onValuesChange={setValues}
      onRemoveRange={selection.removeRange}
      onPatchRange={selection.patchRange}
      onAddRange={(range) =>
        selection.ranges.length === 0
          ? select(apartmentId, range.from, range.to)
          : addRange(range.from, range.to)
      }
      onClear={selection.clear}
      onPushed={() => {
        selection.clear();
        setPushed(true);
        props.onChanged();
      }}
    />
  );
  const grid = (
    <section
      aria-label={`Monat · ${apartment.name}`}
      aria-busy={props.loading || undefined}
      className="bg-card border-hairline rounded-tile shadow-float min-w-0 overflow-clip border md:flex-[999_1_600px]"
    >
      {props.notice}
      <div className={cn(props.loading && 'pointer-events-none opacity-60')}>
        <MonthGrid
          propertyId={apartmentId}
          compact={mobile}
          weeks={weeks}
          currency={apartment.currency}
          today={data.objectToday}
          ranges={selection.ranges}
          onBegin={begin}
          onExtend={canEdit && !props.loading ? selection.extend : undefined}
        />
      </div>
      <CloseoutsOutsideViewNote
        apartment
        count={apartment.channelCloseoutsOutsideView}
        className="m-3"
      />
      <MonthHint canEdit={canEdit} inspectOnly={inspectOnly} mobile={mobile} />
    </section>
  );
  if (mobile) {
    return (
      <MonthMobileActions
        grid={grid}
        side={side || null}
        inspectOnly={inspectOnly}
        pushed={pushed}
        apartmentName={apartment.name}
        ranges={selection.ranges}
        closeouts={data.channelCloseouts.filter((rule) => rule.propertyIds.includes(apartmentId))}
        onManageRules={props.onManageRules}
        extras={props.mobileExtras}
      />
    );
  }
  return <MonthDesktopLayout grid={grid} side={side || null} canEdit={canEdit} />;
}
